import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { AddressProvider } from "@/contexts/AddressContext";
import { BrandLoader } from "@/components/BrandLoader";
import MainLayout from "@/components/Layout/MainLayout";
import Home from "@/pages/Home";
import Restaurants from "@/pages/Restaurants";
import RestaurantDetail from "@/pages/RestaurantDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderTracking from "@/pages/OrderTracking";
import Login from "@/pages/Login";
import PartnerLogin from "@/pages/PartnerLogin";
import Register from "@/pages/Register";
import Profile from "@/pages/Profile";
import RestaurantDashboard from "@/pages/RestaurantDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import GroupDashboard from "@/pages/GroupDashboard";
import AdminConsole from "@/pages/AdminConsole";
import Driver from "@/pages/Driver";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import BecomeDriver from "@/pages/BecomeDriver";
import DriverQuiz from "@/pages/DriverQuiz";
import DriverRules from "@/pages/DriverRules";
import BecomePartner from "@/pages/BecomePartner";
import PartnerProfile from "@/pages/PartnerProfile";
import ChangePassword from "@/pages/ChangePassword";
import AuthCallback from "@/pages/AuthCallback";
import AuthNativeBridge from "@/pages/AuthNativeBridge";
import GoogleCallback from "@/pages/GoogleCallback";
import NativeAuthListener from "@/components/NativeAuthListener";
import IncomingAlertOverlay from "@/components/IncomingAlertOverlay";
import GiftMeal from "@/pages/GiftMeal";
import TrackShared from "@/pages/TrackShared";
import AdminLogin from "@/pages/AdminLogin";
import PaypalReturn from "@/pages/PaypalReturn";
import PaypalCancel from "@/pages/PaypalCancel";
import InstallPrompt from "@/components/InstallPrompt";

function HashAuthCatcher({ children }) {
  const location = useLocation();
  // /auth/native-bridge relaie le session_id vers l'app native — ne pas l'intercepter ici
  if (location.hash?.includes("session_id=") && location.pathname !== "/auth/native-bridge") return <AuthCallback />;
  return children;
}

function ProtectedRoute({ children, roles, allowMustChange }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <BrandLoader message="Chargement de votre espace…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // Force password change on first login (auto-provisioned accounts)
  if (user.must_change_password && !allowMustChange && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

/** Blocage total : tant que le mot de passe temporaire n'est pas changé,
 * TOUTE page (y compris publiques : accueil, restaurants, panier…) redirige
 * vers /change-password. Vérifié à chaque changement de route. */
function MustChangePasswordGate({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

/** Un livreur validé mais qui n'a pas encore réussi le quiz d'intégration
 * ne peut pas accéder à l'écran des courses : redirection vers le quiz. */
function DriverQuizGate({ children }) {
  const { user } = useAuth();
  if (user?.role === "driver" && user?.quiz_passed === false) {
    return <Navigate to="/driver/quiz" replace />;
  }
  return children;
}

/** Remonte en haut de page à chaque changement de route (liens du footer inclus). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  return (
    <HashAuthCatcher>
      <MustChangePasswordGate>
      <NativeAuthListener />
      <IncomingAlertOverlay />
      <ScrollToTop />
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/google" element={<GoogleCallback />} />
        <Route path="/auth/native-bridge" element={<AuthNativeBridge />} />
        <Route element={<MainLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/restaurants" element={<Restaurants />} />
          <Route path="/restaurants/:id" element={<RestaurantDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/orders/:id" element={<ProtectedRoute><OrderTracking /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/espace-partenaires" element={<PartnerLogin />} />
          <Route path="/partner-login" element={<PartnerLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/dashboard/restaurant" element={<ProtectedRoute roles={["restaurant","admin"]}><RestaurantDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/livreur" element={<ProtectedRoute roles={["driver","admin"]}><DriverDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/dashboard/groupe" element={<ProtectedRoute roles={["restaurant_group","admin"]}><GroupDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminConsole /></ProtectedRoute>} />
          <Route path="/driver" element={<ProtectedRoute roles={["driver","admin"]}><DriverQuizGate><Driver /></DriverQuizGate></ProtectedRoute>} />
          <Route path="/driver/quiz" element={<ProtectedRoute roles={["driver"]}><DriverQuiz /></ProtectedRoute>} />
          <Route path="/driver/regles" element={<ProtectedRoute roles={["driver","admin"]}><DriverRules /></ProtectedRoute>} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/confidentialite" element={<Privacy />} />
          <Route path="/devenir-livreur" element={<BecomeDriver />} />
          <Route path="/become-driver" element={<BecomeDriver />} />
          <Route path="/devenir-partenaire" element={<BecomePartner />} />
          <Route path="/become-partner" element={<BecomePartner />} />
          <Route path="/offrir" element={<GiftMeal />} />
          <Route path="/offrir-un-repas" element={<GiftMeal />} />
          <Route path="/gift" element={<GiftMeal />} />
          <Route path="/track/:token" element={<TrackShared />} />
          <Route path="/nd-console" element={<AdminLogin />} />
          <Route path="/paypal/return" element={<PaypalReturn />} />
          <Route path="/paypal/cancel" element={<PaypalCancel />} />
          <Route path="/partner-profile" element={<ProtectedRoute roles={["restaurant","driver","admin"]}><PartnerProfile /></ProtectedRoute>} />
          <Route path="/profil-partenaire" element={<ProtectedRoute roles={["restaurant","driver","admin"]}><PartnerProfile /></ProtectedRoute>} />
          <Route path="/change-password" element={<ProtectedRoute allowMustChange><ChangePassword /></ProtectedRoute>} />
          <Route path="*" element={<Home />} />
        </Route>
      </Routes>
      </MustChangePasswordGate>
    </HashAuthCatcher>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AddressProvider>
          <CartProvider>
            <Toaster
              position="top-right"
              richColors
              closeButton
              duration={3000}
              visibleToasts={2}
              expand={false}
              offset={{ top: 76, right: 16 }}
              mobileOffset={{ top: 76, left: 12, right: 12 }}
            />
            <AppRouter />
            <InstallPrompt />
          </CartProvider>
        </AddressProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
